/* ==========================================================================
   LAKSHIV ENTERPRISE — Predictive search
   Uses Shopify's /search/suggest endpoint rendered through the theme's own
   `predictive-search` section, so results are styled Liquid rather than
   client-side markup.
   ========================================================================== */

(function () {
  'use strict';

  var L = (window.Lakshiv = window.Lakshiv || {});
  var utils = L.utils;
  if (!utils || !L.settings.predictiveSearch) return;

  var MIN_CHARS = 2;
  var cache = {};

  function PredictiveSearch(form) {
    this.form = form;
    this.input = form.querySelector('input[type="search"], input[name="q"]');
    this.results = form.querySelector('[data-predictive-results]');
    this.clearBtn = form.querySelector('[data-search-clear]');
    this.controller = null;

    if (!this.input || !this.results) return;

    this.results.setAttribute('role', 'listbox');
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('autocomplete', 'off');

    this.bind();
  }

  PredictiveSearch.prototype.bind = function () {
    var self = this;

    utils.on(
      this.input,
      'input',
      utils.debounce(function () {
        self.onInput();
      }, 240)
    );

    utils.on(this.input, 'focus', function () {
      if (self.input.value.trim().length >= MIN_CHARS) self.open();
    });

    utils.on(this.input, 'keydown', function (event) {
      self.onKeydown(event);
    });

    utils.on(this.clearBtn, 'click', function () {
      self.input.value = '';
      self.form.classList.remove('has-value');
      self.close();
      self.input.focus();
    });

    // Close when focus or the pointer leaves the whole search component.
    document.addEventListener('click', function (event) {
      if (!self.form.contains(event.target)) self.close();
    });

    document.addEventListener('focusin', function (event) {
      if (!self.form.contains(event.target)) self.close();
    });
  };

  PredictiveSearch.prototype.onInput = function () {
    var term = this.input.value.trim();
    this.form.classList.toggle('has-value', term.length > 0);

    if (term.length < MIN_CHARS) {
      this.close();
      return;
    }

    this.search(term);
  };

  PredictiveSearch.prototype.search = function (term) {
    var self = this;

    if (cache[term]) {
      this.render(cache[term]);
      return;
    }

    // Cancel a slower in-flight request so results can never arrive out of order.
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    this.renderSkeleton();

    var url =
      L.routes.predictiveSearch +
      '?q=' +
      encodeURIComponent(term) +
      '&resources[type]=product,collection,article,page' +
      '&resources[limit]=6' +
      '&resources[options][unavailable_products]=last' +
      '&section_id=predictive-search';

    fetch(url, { signal: this.controller.signal })
      .then(function (res) {
        if (!res.ok) throw new Error('Search failed');
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var markup = doc.querySelector('[data-predictive-inner]');
        var content = markup ? markup.innerHTML : '';
        cache[term] = content;
        self.render(content);
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        self.close();
      });
  };

  PredictiveSearch.prototype.renderSkeleton = function () {
    var row =
      '<div class="predictive__item"><div class="skeleton" style="width:56px;aspect-ratio:1"></div>' +
      '<div style="flex:1"><div class="skeleton skeleton-text"></div>' +
      '<div class="skeleton skeleton-text skeleton-text--sm"></div></div></div>';
    this.results.innerHTML = '<div class="predictive__list">' + row + row + row + '</div>';
    this.open();
  };

  PredictiveSearch.prototype.render = function (html) {
    this.results.innerHTML = html;
    if (html.trim()) {
      this.open();
    } else {
      this.close();
    }
    document.dispatchEvent(
      new CustomEvent('lakshiv:section:loaded', { detail: { container: this.results } })
    );
  };

  PredictiveSearch.prototype.open = function () {
    this.results.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
  };

  PredictiveSearch.prototype.close = function () {
    this.results.classList.remove('is-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
  };

  PredictiveSearch.prototype.items = function () {
    return utils.qsa('.predictive__item', this.results);
  };

  PredictiveSearch.prototype.setActive = function (index) {
    var items = this.items();
    items.forEach(function (item, i) {
      item.classList.toggle('is-active', i === index);
      item.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    this.activeIndex = index;
    if (index >= 0 && items[index]) {
      items[index].scrollIntoView({ block: 'nearest' });
    }
  };

  PredictiveSearch.prototype.onKeydown = function (event) {
    var items = this.items();

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (!items.length || !this.results.classList.contains('is-open')) return;

    var index = typeof this.activeIndex === 'number' ? this.activeIndex : -1;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setActive((index + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setActive((index - 1 + items.length) % items.length);
    } else if (event.key === 'Enter' && index >= 0) {
      event.preventDefault();
      items[index].click();
    }
  };

  /* --- Mobile full-screen search ------------------------------------------ */

  function initMobileSearch() {
    var panel = document.getElementById('MobileSearch');
    if (!panel) return;

    // Opening is handled by the generic [data-open] wiring in theme.js; we only
    // need to focus the field once the panel finishes animating in.
    panel.addEventListener('layer:open', function () {
      var input = panel.querySelector('input[name="q"]');
      if (input) setTimeout(function () { input.focus(); }, 120);
    });
  }

  /* --- Search results page tabs ------------------------------------------- */

  function initResultTabs() {
    utils.qsa('[data-search-tab]').forEach(function (tab) {
      utils.on(tab, 'click', function () {
        var type = tab.getAttribute('data-search-tab');
        utils.qsa('[data-search-tab]').forEach(function (other) {
          other.setAttribute('aria-selected', String(other === tab));
        });
        utils.qsa('[data-search-panel]').forEach(function (panel) {
          panel.hidden = panel.getAttribute('data-search-panel') !== type;
        });
      });
    });
  }

  function init() {
    utils.qsa('[data-predictive-search]').forEach(function (form) {
      new PredictiveSearch(form);
    });
    initMobileSearch();
    initResultTabs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
